export interface SafeApiResponse<T = any> {
  data?: T;
  success?: boolean;
  message?: string;
  error?: any;
}

/**
 * API 응답에서 안전하게 data를 추출
 * @param response API 응답 객체
 * @param defaultValue data가 없을 때 반환할 기본값
 * @returns 안전하게 추출된 data 또는 기본값
 */
export const safeGetData = <T = any>(
  response: any, 
  defaultValue: T = {} as T
): T => {
  if (!response) return defaultValue;
  // Axios 응답 형태: { data: { successFlag, data, message, ... } }
  if (response.data !== undefined) {
    const payload = response.data;
    if (payload && payload.data !== undefined) {
      return payload.data as T;
    }
    return payload as T;
  }
  return defaultValue;
};

/**
 * API 응답이 성공적인지 안전하게 확인
 * @param response API 응답 객체
 * @returns 성공 여부
 */
export const safeIsSuccess = (response: any): boolean => {
  if (!response) return false;
  // 서버 표준 플래그 우선
  if (response.data?.successFlag !== undefined) return Boolean(response.data.successFlag);
  if (response.successFlag !== undefined) return Boolean(response.successFlag);
  if (response.success !== undefined) return Boolean(response.success);
  // HTTP 상태 코드 기반 추정
  if (typeof response.status === 'number') return response.status >= 200 && response.status < 300;
  // Axios 응답 객체에 data가 있으면 성공으로 간주 (최소 가드)
  if (response.data !== undefined) return true;
  return false;
};

/**
 * API 응답에서 안전하게 메시지를 추출
 * @param response API 응답 객체
 * @param defaultMessage 기본 메시지
 * @returns 안전하게 추출된 메시지
 */
export const safeGetMessage = (
  response: any, 
  defaultMessage: string = '요청을 처리할 수 없습니다.'
): string => {
  if (!response) return defaultMessage;
  // 서버 메시지 우선 탐색
  const serverMsg = response.data?.detailMessage || response.data?.message;
  if (serverMsg) return serverMsg;
  // 일반 메시지/에러 객체
  return response.message || response.error?.message || defaultMessage;
};

/**
 * API 호출을 안전하게 래핑
 * @param apiCall API 호출 함수
 * @param defaultValue 실패 시 반환할 기본값
 * @returns Promise<SafeApiResponse>
 */
export const safeApiCall = async <T = any>(
  apiCall: () => Promise<any>,
  defaultValue: T = {} as T
): Promise<SafeApiResponse<T>> => {
  try {
    const response = await apiCall();
    return {
      data: safeGetData(response, defaultValue),
      success: safeIsSuccess(response),
      message: safeGetMessage(response),
    };
  } catch (error) {
    console.error('API call failed:', error);
    return {
      data: defaultValue,
      success: false,
      message: (error as any)?.response?.data?.message || (error instanceof Error ? error.message : '알 수 없는 오류가 발생했습니다.'),
      error,
    };
  }
};
